import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import * as api from '../../../app/axios/api'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import {
  ArrowLeft,
  User,
  BookOpen,
  Calendar,
  Hash,
  IndianRupee,
  Copy,
  CheckCircle,
  Clock,
  AlertCircle,
  ShieldCheck,
  Printer,
  Download,
} from 'lucide-react'

const StudentDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [student, setStudent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [copiedField, setCopiedField] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)

  useEffect(() => {
    const fetchStudentDetails = async () => {
      setLoading(true)
      try {
        const res = await api.fetchSummerDashboard()
        const allStudents = res.data || []
        const foundStudent = allStudents.find((s) => s.id === parseInt(id))

        if (foundStudent) setStudent(foundStudent)
        else setError('Student details not found in the system.')
      } catch (err) {
        console.error('Error fetching details:', err)
        setError('Failed to fetch details. Please try again later.')
      } finally {
        setLoading(false)
      }
    }
    fetchStudentDetails()
  }, [id])

  const handleCopy = (text, fieldName) => {
    if (!text) return
    navigator.clipboard.writeText(text)
    setCopiedField(fieldName)
    setTimeout(() => setCopiedField(null), 2000)
  }

  // --- Print Receipt Logic ---
  const handlePrint = () => {
    window.print()
  }

  // --- Download PDF Receipt Logic ---
  const handleDownloadPdf = async () => {
    setIsDownloading(true)
    try {
      const receiptElement = document.getElementById('printable-receipt')

      // Temporarily visible for html2canvas
      receiptElement.classList.remove('hidden')
      receiptElement.style.position = 'absolute'
      receiptElement.style.top = '-9999px'

      const canvas = await html2canvas(receiptElement, {
        scale: 2,
        useCORS: true,
      })
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)
      pdf.save(`${student.name.replace(/\s+/g, '_')}_Receipt.pdf`)

      // Re-hide after generating
      receiptElement.style.position = ''
      receiptElement.style.top = ''
      receiptElement.classList.add('hidden')
    } catch (err) {
      console.error('Failed to generate PDF', err)
      alert('Failed to download PDF. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  const getInitials = (name) => {
    if (!name) return 'ST'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase()
  }

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4'></div>
        <p className='text-lg text-gray-600 font-medium'>
          Fetching student details...
        </p>
      </div>
    )
  }

  if (error || !student) {
    return (
      <div className='min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center'>
        <div className='bg-white p-8 rounded-2xl shadow-sm border border-red-100 max-w-md w-full'>
          <AlertCircle className='text-red-500 w-16 h-16 mx-auto mb-4' />
          <h2 className='text-2xl font-bold text-gray-800 mb-2'>Oops!</h2>
          <p className='text-gray-600 mb-6'>{error}</p>
          <button
            onClick={() => navigate(-1)}
            className='px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center justify-center gap-2 w-full font-medium'
          >
            <ArrowLeft size={18} /> Go Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  const isPaid = student.status?.toLowerCase() === 'paid'
  const regDate = new Date(student.created_at).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

  return (
    <>
      {/* 🟢 MAIN DASHBOARD UI (Hidden on Print) */}
      <div className='print:hidden min-h-screen bg-gray-50/50 p-4 md:p-6 lg:p-8 font-sans'>
        <div className='max-w-6xl mx-auto space-y-6'>
          {/* Top Navigation & Action Buttons */}
          <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
            <button
              onClick={() => navigate(-1)}
              className='flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition font-medium text-sm bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm'
            >
              <ArrowLeft size={18} /> Back to Dashboard
            </button>

            {/* Print & Download Buttons */}
            <div className='flex items-center gap-3'>
              {/* <button
                onClick={handlePrint}
                className='flex items-center gap-2 text-gray-700 bg-white hover:bg-gray-50 transition font-medium text-sm px-4 py-2 rounded-lg border border-gray-300 shadow-sm'
              >
                <Printer size={18} /> Print
              </button> */}
              <button
                onClick={handleDownloadPdf}
                disabled={isDownloading}
                className='flex items-center gap-2 text-white bg-indigo-600 hover:bg-indigo-700 transition font-medium text-sm px-5 py-2 rounded-lg border border-indigo-700 shadow-sm disabled:opacity-70'
              >
                {isDownloading ? (
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                ) : (
                  <Download size={18} />
                )}
                {isDownloading ? 'Downloading...' : 'Download Receipt'}
              </button>
            </div>
          </div>

          {/* Profile Header Card */}
          <div className='bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8 relative overflow-hidden'>
            <div className='absolute top-0 right-0 w-64 h-64 bg-indigo-50 rounded-full blur-3xl -mr-20 -mt-20 opacity-60'></div>

            <div className='relative flex flex-col md:flex-row gap-6 items-start md:items-center justify-between'>
              <div className='flex items-center gap-5'>
                <div className='w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl md:text-3xl font-bold shadow-lg shrink-0'>
                  {getInitials(student.name)}
                </div>
                <div>
                  <h1 className='text-2xl md:text-4xl font-extrabold text-gray-900 capitalize tracking-tight'>
                    {student.name}
                  </h1>
                  <div className='flex flex-wrap items-center gap-3 mt-3 text-sm text-gray-600 font-medium'>
                    <span className='flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded-full text-xs font-bold text-gray-700 border border-gray-200'>
                      Reg No: {student.registration_no || ' Pending'}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border shadow-sm ${student.training_mode?.toLowerCase() === 'online' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${student.training_mode?.toLowerCase() === 'online' ? 'bg-emerald-500' : 'bg-orange-500'}`}
                      ></span>
                      {student.training_mode} Mode
                    </span>
                    <span className='flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1.5 rounded-full text-xs font-bold shadow-sm'>
                      <Calendar size={14} /> {regDate}
                    </span>
                  </div>
                </div>
              </div>

              <div className='flex flex-wrap gap-3 mt-4 md:mt-0'>
                <div
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${isPaid ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}
                >
                  {isPaid ? <CheckCircle size={20} /> : <Clock size={20} />}
                  <div>
                    <p className='text-[10px] uppercase tracking-wider font-bold opacity-70'>
                      Payment Status
                    </p>
                    <p className='text-sm font-bold capitalize'>
                      {student.status || 'Created'}
                    </p>
                  </div>
                </div>
                <div className='flex items-center gap-2 px-4 py-2 rounded-xl border bg-indigo-50 border-indigo-100 text-indigo-700'>
                  <BookOpen size={20} />
                  <div>
                    <p className='text-[10px] uppercase tracking-wider font-bold opacity-70'>
                      Course Code
                    </p>
                    <p className='text-sm font-bold uppercase'>
                      {student.course_code}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Data Grid */}
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
            <div className='lg:col-span-2'>
              <div className='bg-white rounded-2xl shadow-sm border border-gray-200 p-6 h-full'>
                <div className='flex items-center gap-2 mb-6 border-b border-gray-100 pb-4'>
                  <div className='p-2 bg-blue-50 text-blue-600 rounded-lg'>
                    <User size={20} />
                  </div>
                  <h2 className='text-lg font-bold text-gray-800'>
                    Personal & Academic Information
                  </h2>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-8'>
                  <DetailItem label='Full Name' value={student.name} />
                  <DetailItem
                    label="Father's Name"
                    value={student.father_name}
                  />
                  <DetailItem label='Gender' value={student.gender} />
                  <DetailItem
                    label='Phone Number'
                    value={student.phone_number}
                    copyable
                    onCopy={() => handleCopy(student.phone_number, 'phone')}
                    copied={copiedField === 'phone'}
                  />
                  <DetailItem
                    label='Course Code'
                    value={
                      <span className='bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md font-bold text-sm tracking-wide border border-indigo-100'>
                        {student.course_code}
                      </span>
                    }
                  />
                  <DetailItem
                    label='Course Year'
                    value={
                      student.course_year
                        ? `Year ${student.course_year}`
                        : 'N/A'
                    }
                  />
                  <div className='sm:col-span-2'>
                    <DetailItem
                      label='College / University'
                      value={student.college}
                    />
                  </div>
                  <div className='sm:col-span-2'>
                    <DetailItem label='Full Address' value={student.address} />
                  </div>
                </div>
              </div>
            </div>

            <div className='space-y-6'>
              <div className='bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden'>
                <div className='p-6 bg-slate-50 border-b border-gray-200'>
                  <div className='flex items-center gap-2 mb-2'>
                    <div className='p-2 bg-green-100 text-green-700 rounded-lg'>
                      <IndianRupee size={20} />
                    </div>
                    <h2 className='text-lg font-bold text-gray-800'>
                      Fee Breakdown
                    </h2>
                  </div>
                </div>
                <div className='p-6 space-y-4'>
                  <div className='flex justify-between items-center text-sm'>
                    <span className='text-gray-500 font-medium'>
                      Total Course Fee
                    </span>
                    <span className='font-bold text-gray-800'>
                      ₹{student.fee || 0}
                    </span>
                  </div>
                  <div className='flex justify-between items-center text-sm'>
                    <span className='text-gray-500 font-medium'>
                      Registration Fee
                    </span>
                    <span className='font-bold text-gray-800'>
                      ₹{student.registration_fee || 0}
                    </span>
                  </div>
                  <div className='pt-4 mt-2 border-t border-gray-200'>
                    <p className='text-[11px] text-gray-400 uppercase tracking-wide font-bold mb-1'>
                      Net Payable Amount
                    </p>
                    <p className='text-3xl font-black text-gray-900'>
                      ₹{(Number(student.fee) || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              </div>

              <div className='bg-white rounded-2xl shadow-sm border border-gray-200 p-6'>
                <div className='flex items-center gap-2 mb-6 border-b border-gray-100 pb-4'>
                  <div className='p-2 bg-gray-100 text-gray-700 rounded-lg'>
                    <ShieldCheck size={20} />
                  </div>
                  <h2 className='text-lg font-bold text-gray-800'>
                    System & Payment
                  </h2>
                </div>
                <div className='space-y-5'>
                  <DetailItem
                    label='Razorpay Order ID'
                    value={student.razorpay_order_id || 'Not Generated'}
                    isCode
                    copyable={!!student.razorpay_order_id}
                    onCopy={() =>
                      handleCopy(student.razorpay_order_id, 'order')
                    }
                    copied={copiedField === 'order'}
                  />
                  <DetailItem
                    label='Razorpay Payment ID'
                    value={student.razorpay_payment_id || 'Pending'}
                    isCode
                    copyable={!!student.razorpay_payment_id}
                    onCopy={() =>
                      handleCopy(student.razorpay_payment_id, 'payment')
                    }
                    copied={copiedField === 'payment'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🖨️ PRINTABLE RECEIPT MODEL (Inflection Org Pvt Ltd) */}
      <div
        id='printable-receipt'
        className='hidden print:block w-full max-w-[700px] bg-white text-black shadow-lg border border-gray-300 print:shadow-none print:border-none'
        style={{
          fontFamily: 'Georgia, serif',
          margin: '0 auto',
          background: 'white',
        }}
      >
        {/* Header Section */}
        <div className='p-4 border-b-2 border-[#007adf] flex items-center justify-between print:border-[#007adf]'>
          {/* Logo */}
          <div className='w-16 h-16 flex items-center justify-center shrink-0'>
            <img
              src='/inflectionORG.webp'
              alt='Logo'
              className='w-full h-full object-contain'
            />
          </div>

          {/* Institute Details */}
          <div className='flex-1 text-center px-2'>
            <h1 className='text-2xl font-black text-[#007adf] uppercase leading-none tracking-wide print:text-[#007adf]'>
              Inflection Org Pvt Ltd
            </h1>
            <p className='text-[10px] font-bold text-gray-800 italic uppercase tracking-widest mt-1'>
              The Next Gen Computer & Robotics Institute
            </p>
            <div className='mt-1 text-[9px] text-gray-700 font-sans leading-tight'>
              <p>
                Manaurveer Khamaria, Bhadohi, UP - 221306 | Email:
                team@inflection.org.in
              </p>
              <p className='font-semibold'>
                Contact: +91 9369568303, 7395081624
              </p>
            </div>
          </div>

          <div className='w-16 shrink-0'></div>
        </div>

        {/* Highlighted Title Bar */}
        <div className='bg-[#007adf]/10 py-1.5 border-b border-[#007adf]/30 text-center print:bg-[#007adf]/10 print:-webkit-print-color-adjust: exact;'>
          <h2 className='text-[11px] font-black text-[#007adf] uppercase tracking-[0.15em]'>
            Summer Training Registration Receipt
          </h2>
        </div>

        {/* Content Body */}
        <div className='p-6 space-y-4 font-sans'>
          {/* Registration & Date Row */}
          <div className='flex justify-between items-end border-b border-gray-200 pb-2'>
            <div className='leading-none'>
              <p className='text-[9px] font-bold text-gray-500 uppercase tracking-wide'>
                Reg. Number
              </p>
              <p className='text-lg font-black text-[#007adf] mt-1 print:text-[#007adf]'>
                {student.registration_no || 'Pending'}
              </p>
            </div>
            <div className='text-right leading-none'>
              <p className='text-[9px] font-bold text-gray-500 uppercase tracking-wide'>
                Date
              </p>
              <p className='text-sm font-bold mt-1 text-gray-900'>
                {new Date(student.created_at).toLocaleDateString('en-GB')}
              </p>
            </div>
          </div>

          {/* Student Details Grid */}
          <div className='grid grid-cols-2 gap-x-8 gap-y-2.5 py-1.5 text-[13px] text-gray-900'>
            <div className='border-b border-gray-100 pb-1'>
              <p className='text-[8px] font-bold text-gray-400 uppercase'>
                Student Name
              </p>
              <p className='font-bold uppercase leading-tight'>
                {student.name}
              </p>
            </div>
            <div className='border-b border-gray-100 pb-1'>
              <p className='text-[8px] font-bold text-gray-400 uppercase'>
                Father's Name
              </p>
              <p className='font-bold uppercase leading-tight'>
                {student.father_name}
              </p>
            </div>
            <div className='border-b border-gray-100 pb-1'>
              <p className='text-[8px] font-bold text-gray-400 uppercase'>
                Course / Batch
              </p>
              <p className='font-bold leading-tight'>{student.course_code}</p>
            </div>
            <div className='border-b border-gray-100 pb-1'>
              <p className='text-[8px] font-bold text-gray-400 uppercase'>
                Mode
              </p>
              <p className='font-bold leading-tight'>{student.training_mode}</p>
            </div>
            <div className='col-span-2 border-b border-gray-100 pb-1'>
              <p className='text-[8px] font-bold text-gray-400 uppercase'>
                College / Institution
              </p>
              <p className='font-bold leading-tight'>{student.college}</p>
            </div>
          </div>

          {/* Fee Table */}
          <div className='mt-3 border border-gray-300 rounded overflow-hidden'>
            <table className='w-full text-[13px] text-left'>
              <thead className='bg-gray-100 border-b border-gray-300 font-bold text-gray-700 print:bg-gray-100 print:-webkit-print-color-adjust: exact;'>
                <tr>
                  <th className='px-3 py-2 uppercase text-[10px] tracking-wide'>
                    Description
                  </th>
                  <th className='px-3 py-2 text-right uppercase text-[10px] tracking-wide'>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                <tr>
                  <td className='px-3 py-2 font-semibold text-gray-800'>
                    Total Course Fee
                  </td>
                  <td className='px-3 py-2 text-right font-bold text-gray-900'>
                    ₹{student.fee || 0}.00
                  </td>
                </tr>
                <tr className='bg-[#007adf]/5 print:bg-[#007adf]/5 print:-webkit-print-color-adjust: exact;'>
                  <td className='px-3 py-2 font-black uppercase text-[11px] text-gray-900'>
                    Net Paid Amount (Reg. Fee)
                  </td>
                  <td className='px-3 py-2 text-right font-black text-[#007adf] text-[15px] print:text-[#007adf]'>
                    ₹{student.registration_fee || 0}.00
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer Info & Signatory */}
          <div className='flex justify-between items-end pt-6'>
            <div className='text-[9px] text-gray-500 font-sans uppercase leading-tight'>
              <p className='font-bold text-gray-700'>
                Status: <span className='text-green-600'>{student.status}</span>
              </p>
              <p className='italic mt-1.5 text-[8px] capitalize'>
                * This is a computer-generated receipt.
              </p>
            </div>

            <div className='text-center w-40'>
              <div className='h-10'></div> {/* Signature Space */}
              <div className='border-t border-gray-800 pt-1'>
                <p className='text-[10px] font-black text-gray-900 uppercase tracking-wide'>
                  Prakhar Maurya
                </p>
                <p className='text-[8px] font-bold text-gray-500 uppercase'>
                  Authorized Signatory
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm; /* Paper margins */
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body * {
            visibility: hidden;
          }
          #printable-receipt, #printable-receipt * {
            visibility: visible;
          }
          #printable-receipt {
            position: absolute;
            top: 2%;
            left: 50%;
            transform: translateX(-50%);
            width: 85%; /* Guaranteed Left/Right Margins */
            max-width: 700px;
          }
        }
      `}</style>
    </>
  )
}

// ---------------------------------------------------------
// Detail Item Component
// ---------------------------------------------------------
const DetailItem = ({ label, value, isCode, copyable, onCopy, copied }) => (
  <div className='group'>
    <p className='text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1'>
      {label}
    </p>
    <div className='flex items-start gap-2'>
      <div
        className={`text-sm md:text-base font-medium text-gray-800 capitalize ${isCode ? 'font-mono text-xs break-all lowercase text-indigo-700 bg-indigo-50 px-2 py-1 rounded' : ''}`}
      >
        {value || <span className='text-gray-400 italic'>Not Provided</span>}
      </div>
      {copyable && value && (
        <button
          onClick={onCopy}
          className='text-gray-400 hover:text-indigo-600 transition p-1 rounded-md hover:bg-indigo-50 opacity-0 group-hover:opacity-100 focus:opacity-100'
        >
          {copied ? (
            <CheckCircle size={16} className='text-green-500' />
          ) : (
            <Copy size={16} />
          )}
        </button>
      )}
    </div>
  </div>
)

export default StudentDetails
